import React from 'react';
import PropTypes from 'prop-types';
import {ParentBox} from '../box-utils/ParentBox.react.js';
import BoxDecorator from '../box-utils/BoxDecorator.react';
import {defineMessages, IntlShape} from 'react-intl';
import {viewWithDataGetPrimaryData} from '../box-utils/PrimaryData';
import PanelColumns from './system/PanelColumns.react';
import StatusIcon from './StatusIcon.react';


const messages = defineMessages({
    catchAll: {id: 'scenario.screen.basicEmailCheck.reason.catchAll', 'defaultMessage': 'This domain has catch-all address, that receives all wrongly addressed emails for the domain.'},
    freemail: {id: 'scenario.screen.basicEmailCheck.reason.freemail', 'defaultMessage': 'Address from free email provider.'},
    role: {id: 'scenario.screen.basicEmailCheck.reason.role', 'defaultMessage': 'Typically company address defined by a job rather than a person, often managed by several people.'},
    personalAddress: {id: 'scenario.screen.basicEmailCheck.reason.personalAddress', 'defaultMessage': 'Not address of single person.'},
    disposable: {id: 'scenario.screen.basicEmailCheck.reason.disposable', 'defaultMessage': 'An unique address is used for every contact or entity and could be easily cancelled.'},
    greylisted: {id: 'scenario.screen.basicEmailCheck.reason.greylisted', 'defaultMessage': 'The mail server uses greylisting where an email has to be sent a second time at a later time.'},
    validMxRecord: {id: 'scenario.screen.basicEmailCheck.reason.validMxRecord', 'defaultMessage': 'Address has not a valid MX record in its DNS entries.'},
    validSyntax: {id: 'scenario.screen.basicEmailCheck.reason.validSyntax', 'defaultMessage': 'Email address is not syntactically correct.'},
    smtpServerResponding: {id: 'scenario.screen.basicEmailCheck.reason.smtpServerResponding', 'defaultMessage': 'The mail servers specified in the MX records are not responding to connections.'},
    smtpServerAcceptsAddress: {id: 'scenario.screen.basicEmailCheck.reason.smtpServerAcceptsAddress', 'defaultMessage': 'The mail server doesn\'t confirm an existence of the email address.'},
    spam: {id: 'scenario.screen.basicEmailCheck.reason.spam', 'defaultMessage': 'Address was reported as source of spam.'},
    gibberish: {id: 'scenario.screen.basicEmailCheck.reason.gibberish', 'defaultMessage': 'Email address looks as an automatically generated.'},
    suppressed: {id: 'scenario.screen.basicEmailCheck.reason.suppressed', 'defaultMessage': 'Address is present in MailboxValidator\'s blacklist.'},
});

const messagesShort = defineMessages({
    catchAll: {id: 'scenario.screen.BasicEmailCheck.reason.short.catchAll', 'defaultMessage': 'Catch-all'},
    freemail: {id: 'scenario.screen.BasicEmailCheck.reason.short.freemail', 'defaultMessage': 'Freemail'},
    role: {id: 'scenario.screen.BasicEmailCheck.reason.short.role', 'defaultMessage': 'Role-address'},
    personalAddress: {id: 'scenario.screen.BasicEmailCheck.reason.short.personalAddress', 'defaultMessage': 'Not-personal '},
    disposable: {id: 'scenario.screen.BasicEmailCheck.reason.short.disposable', 'defaultMessage': 'Disposable'},
    greylisted: {id: 'scenario.screen.BasicEmailCheck.reason.short.greylisted', 'defaultMessage': 'Greylisted'},
    validMxRecord: {id: 'scenario.screen.BasicEmailCheck.reason.short.validMxRecord', 'defaultMessage': 'MX record problem'},
    validSyntax: {id: 'scenario.screen.BasicEmailCheck.reason.short.validSyntax', 'defaultMessage': 'Invalid syntax'},
    smtpServerResponding: {id: 'scenario.screen.BasicEmailCheck.reason.short.smtpServerResponding', 'defaultMessage': 'Not responding SMTP server'},
    smtpServerAcceptsAddress: {id: 'scenario.screen.BasicEmailCheck.reason.short.smtpServerAcceptsAddress', 'defaultMessage': 'SMTP server rejects address'},
    spam: {id: 'scenario.screen.BasicEmailCheck.reason.short.spam', 'defaultMessage': 'Spam history'},
    gibberish: {id: 'scenario.screen.BasicEmailCheck.reason.short.gibberish', 'defaultMessage': 'Gibberish'},
    suppressed: {id: 'scenario.screen.BasicEmailCheck.reason.short.suppressed', 'defaultMessage': 'Suppressed'},
});

export default class BasicEmailCheck extends ParentBox {
	static handlerName = "basic-email-check";

    static propTypes = {
        intl: PropTypes.object.isRequired,
        isPreview: PropTypes.bool,
        viewWithData: PropTypes.object.isRequired,
    };

    static rules = {
        "com.dativery.model.entities.emailAddress@catchAll": {"key": "catchAll", "icon": StatusIcon.WARN, "showWhenValue": true, "uid":"f37335d0-3822-487f-956c-7957affa35e3"},
        "com.dativery.model.entities.emailAddress@freemail": {"key": "freemail", "icon": StatusIcon.WARN, "showWhenValue": true, "uid":"009e7788-3bb6-4aa7-8917-e1023c83ccd1"},
        "com.dativery.model.entities.emailAddress@role": {"key": "role", "icon": StatusIcon.OK, "showWhenValue": true, "uid":"aa772016-275c-4650-9ff3-3885e1b38bb8"},
        "com.dativery.model.entities.emailAddress@personalAddress": {"key": "personalAddress", "icon": StatusIcon.OK, "showWhenValue": false, "uid":"f11dc5f2-e364-4cd8-9bd2-d34feedca0de"},
        "com.dativery.model.entities.emailAddress@disposable": {"key": "disposable", "icon": StatusIcon.WARN, "showWhenValue": true, "uid":"c7bc3864-95b7-4a0a-9a31-bbdfbbe2fbda"},
        "com.dativery.model.entities.emailAddress@greylisted": {"key": "greylisted", "icon": StatusIcon.WARN, "showWhenValue": true, "uid":"4afd14a4-1622-4527-9dba-2f67d62c9064"},
        "com.dativery.model.entities.emailAddress@validMxRecord": {"key": "validMxRecord", "icon": StatusIcon.ERROR, "showWhenValue": false, "uid":"7429cd9f-be5c-44a7-83a6-3fa6d1b20da7"},
        "com.dativery.model.entities.emailAddress@validSyntax": {"key": "validSyntax", "icon": StatusIcon.ERROR, "showWhenValue": false, "uid":"b1743e72-3de9-4f78-b6ad-44de7ca01322"},
        "com.dativery.model.entities.emailAddress@smtpServerResponding": {"key": "smtpServerResponding", "icon": StatusIcon.ERROR, "showWhenValue": false, "uid":"8f527ace-46de-4f36-bea5-b3be7d69ac35"},
        "com.dativery.model.entities.emailAddress@smtpServerAcceptsAddress": {"key": "smtpServerAcceptsAddress", "icon": StatusIcon.ERROR, "showWhenValue": false, "uid":"977cd51e-6639-436c-ae3e-f69d384964df"},
        "com.dativery.model.entities.emailAddress@spam": {"key": "spam", "icon": StatusIcon.WARN, "showWhenValue": true, "uid":"9a8cc54f-715a-4ba1-971b-29b04fcb4c98"},
        "io.hunter.emailverifier.emailAddresses@gibberish": {"key": "gibberish", "icon": StatusIcon.WARN, "showWhenValue": true, "uid":"a211cbe1-34c2-4c0f-9391-11663787fd8f"},
        "com.mailboxvalidator.emailAddresses@suppressed": {"key": "suppressed", "icon": StatusIcon.WARN, "showWhenValue": true, "uid":"bc19baa5-480e-47a1-95ab-e82ec9c70845"},
    };

	static previewData = [
        {
            "com.dativery.model.entities.emailAddress@domainPart": "example.org",
            "com.dativery.model.entities.emailAddress@emailAddress": "valid@example.org",
            "com.dativery.model.entities.emailAddress@localPart": "valid",
            "com.dativery.model.entities.emailAddress@role": true,
        }, {
            "com.dativery.model.entities.emailAddress@domainPart":"example.com",
            "com.dativery.model.entities.emailAddress@emailAddress":"invalid@example.com",
            "com.dativery.model.entities.emailAddress@localPart":"invalid",
            "com.dativery.model.entities.emailAddress@validSyntax": false,
        }, {
            "com.dativery.model.entities.emailAddress@domainPart":"example.com",
            "com.dativery.model.entities.emailAddress@emailAddress":"greylisted@example.com",
            "com.dativery.model.entities.emailAddress@localPart":"greylisted",
            "com.dativery.model.entities.emailAddress@greylisted": true,
            "com.dativery.model.entities.emailAddress@freemail": true,
        }, {
            "com.dativery.model.entities.emailAddress@domainPart":"example.com",
            "com.dativery.model.entities.emailAddress@emailAddress":"suppresed@example.com",
            "com.mailboxvalidator.emailAddresses@suppressed": true,
        },
    ];

    getIcon() {
        return 'fa-at';
    }

	getServiceName() {
		let entityViewDef = this.props.viewWithData.view.entityViewDef;

		return entityViewDef ? entityViewDef.title : '';
	}

    getMessage(key) {
        if (key in messages) {
            return this.props.intl.formatMessage(messages[key]);
        }
        return null;
    }

    getMessageShort(key) {
        if (key in messagesShort) {
            return this.props.intl.formatMessage(messagesShort[key]);
        }
        return null;
    }


    showData(identificator, value) {
		let rule = BasicEmailCheck.rules[identificator];

        if (rule && rule["showWhenValue"] === value) {
			let {key} = rule;

            return {
				"message": (<span key={key} title={this.getMessage(key)}>{this.getMessageShort(key)}</span>),
				"icon": rule["icon"]
			};
        }

        return {
			"message": null,
			"icon": StatusIcon.OK
		};
    }

    renderStatus(emailValues) {
        let messages = [];
        let statusIcon = StatusIcon.OK;

        Object.keys(emailValues).forEach((identificator)=> {
            let {message, icon} = this.showData(identificator, emailValues[identificator]);

            if (message) {
                messages.push(message);
            }

            if (statusIcon === StatusIcon.OK) {
				// if icon is OK, override it everytime
                statusIcon = icon;
            } else if (statusIcon === StatusIcon.WARN && icon === StatusIcon.ERROR) {
				// if icon is WARN, override it only with ERROR
                statusIcon = icon;
            }
        });

        return (<span><span><StatusIcon type={statusIcon} /></span> {messages.length ? messages.reduce((prev, curr) => [prev, ', ', curr]) : null}</span>);
	}

	renderEmail(emailValue, idx) {
        if (emailValue["com.dativery.model.entities.emailAddress@emailAddress"]) {
            return (
                <div key={idx}>
                    <strong>{emailValue["com.dativery.model.entities.emailAddress@emailAddress"]}</strong> {this.renderStatus(emailValue)}<br/>
                </div>
            );
        } else {
            return null;
        }
	}

	render() {
		let data = this.props.isPreview ? BasicEmailCheck.previewData : viewWithDataGetPrimaryData(this.props.viewWithData);

        return (
            <BoxDecorator className="email-check" icon={this.getIcon()} isPreview={this.props.isPreview} padding title={this.getServiceName()}>
				<PanelColumns className="boxOneTimeFontSize">
					{data.map((emailValue, idx) => {
                        return this.renderEmail(emailValue, idx);
					})}
				</PanelColumns>
            </BoxDecorator>
        );
	}
}
